'use client';

import { useCallback, useEffect, useState } from 'react';

import { useDispatchAction } from '../../contexts/video-player';
import {
  type AnyVideo,
  VideoType,
  fetchClipLikes,
  formatCount,
  likeClip,
  unlikeClip,
} from '../../functions';
import { ActionButton } from './ActionButton';

type LikeActionProps = {
  video: AnyVideo;
  currentUserId?: string;
  isRealEstateListing?: boolean;
  className?: string;
};

export function LikeAction({
  video,
  currentUserId,
  isRealEstateListing,
  className,
}: LikeActionProps) {
  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const dispatchAction = useDispatchAction();

  const clipId =
    video.type === VideoType.Clips
      ? (video.clipId ?? video.id)
      : video.type === VideoType.Review
        ? (video.messageId ?? video.id)
        : video.id;

  // Fetch initial like status
  useEffect(() => {
    let cancelled = false;

    async function load() {
      const result = await fetchClipLikes([clipId], currentUserId);
      if (!cancelled) {
        setIsLiked(result.hasLiked);
        setLikeCount(result.likes);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [clipId, currentUserId]);

  if (video.type !== VideoType.Clips && video.type !== VideoType.Review) {
    return null;
  }

  const isGuestOnRealEstate = isRealEstateListing === true && !currentUserId;

  const handleToggle = useCallback(async () => {
    if (isLoading || isGuestOnRealEstate) return;

    // Guest user — defer to host (e.g. `usePresentPopStoreLogin`).
    if (!currentUserId) {
      dispatchAction({ type: 'requireLogin' });
      return;
    }

    setIsLoading(true);

    const newLiked = !isLiked;
    setIsLiked(newLiked);
    setLikeCount(prev => (newLiked ? prev + 1 : Math.max(0, prev - 1)));

    try {
      if (newLiked) {
        await likeClip(clipId, currentUserId);
      } else {
        await unlikeClip(clipId, currentUserId);
      }
    } catch {
      // Revert on error
      setIsLiked(!newLiked);
      setLikeCount(prev => (newLiked ? prev - 1 : prev + 1));
    } finally {
      setIsLoading(false);
    }
  }, [isLiked, isLoading, clipId, currentUserId]);

  return (
    <ActionButton
      onClick={handleToggle}
      disabled={isLoading || isGuestOnRealEstate}
      className={className}
      label={likeCount > 0 ? `${formatCount(likeCount)} Likes` : undefined}
    >
      <svg fill="none" viewBox="0 0 24 24" width={24} height={24}>
        <path
          d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
          fill={isLiked ? '#FC344D' : '#FFFFFF'}
        />
      </svg>
    </ActionButton>
  );
}
